import { useEffect, useState } from "react"
import { Button, Card, Divider, Flex, Form, Input, InputNumber, Modal, Radio, Segmented } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { Text } from "ui/Typography"
import { spacings } from "themes/variables"
import { AFFECTS, PLACEHOLDER_TICKET_TYPES, PRICE_DIRECTION, PRICE_TYPE, PRICE_TYPE_OPTIONS } from "../../../constants/pricingConstants"
import styles from "./AddDayAdjustmentModal.module.css"

export const APPLIES_ON = { WEEKDAYS: "weekdays", WEEKEND: "weekend", CUSTOM: "custom" }

// Lowercase 3-letter values to match the backend contract (custom_days: ["mon","fri"]);
// displayed capitalized via CSS (.chip text-transform).
const WEEKDAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"]

const AddDayAdjustmentModal = ({ open, editingItem, ticketTypes, onCancel, onSave }) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [appliesOn, setAppliesOn] = useState(APPLIES_ON.WEEKEND)
  const [customDays, setCustomDays] = useState([])
  const [affects, setAffects] = useState(AFFECTS.ALL)
  const [selectedTickets, setSelectedTickets] = useState([])
  const [direction, setDirection] = useState(PRICE_DIRECTION.INCREASE)
  const [priceType, setPriceType] = useState(PRICE_TYPE.PERCENT)

  useEffect(() => {
    if (open && editingItem) {
      form.setFieldsValue({ name: editingItem.name, priceValue: editingItem.priceValue })
      setAppliesOn(editingItem.appliesOn || APPLIES_ON.WEEKEND)
      setCustomDays(editingItem.customDays || [])
      setAffects(editingItem.affects || AFFECTS.ALL)
      setSelectedTickets(editingItem.selectedTickets || [])
      setDirection(editingItem.direction || PRICE_DIRECTION.INCREASE)
      setPriceType(editingItem.priceType || PRICE_TYPE.PERCENT)
    } else if (!open) {
      form.resetFields()
      setAppliesOn(APPLIES_ON.WEEKEND)
      setCustomDays([])
      setAffects(AFFECTS.ALL)
      setSelectedTickets([])
      setDirection(PRICE_DIRECTION.INCREASE)
      setPriceType(PRICE_TYPE.PERCENT)
    }
  }, [open, editingItem, form])

  const toggleDay = (day) =>
    setCustomDays((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]))

  const toggleTicket = (id) =>
    setSelectedTickets((prev) => (prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]))

  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      onSave({
        ...(editingItem ? { id: editingItem.id } : {}),
        ...values,
        appliesOn,
        customDays: appliesOn === APPLIES_ON.CUSTOM ? customDays : [],
        affects,
        selectedTickets: affects === AFFECTS.SELECTED ? selectedTickets : [],
        direction,
        priceType,
      })
    } catch {
      // AntD shows inline field errors
    }
  }

  const footer = (
    <Flex gap={spacings.S}>
      <Button shape="round" color="primary" variant="outlined" size="large" onClick={onCancel}>
        {t("experiences.pricing.adjustment_modal.cancel")}
      </Button>
      <Button shape="round" color="primary" variant="solid" size="large" onClick={handleSave}>
        {t("experiences.pricing.adjustment_modal.save")}
      </Button>
    </Flex>
  )

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={t("experiences.pricing.day_adjustment_modal.title")}
      width={1040}
      footer={footer}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={(label, { required }) => (
          <>
            {label}
            {required && " *"}
          </>
        )}
      >
        <Card className={styles.contentCard}>
          <Flex vertical gap={spacings.XL}>
            <Form.Item
              name="name"
              label={t("experiences.pricing.adjustment_modal.name_label")}
              required
              rules={[{ required: true, message: t("experiences.pricing.adjustment_modal.name_required") }]}
              tooltip={t("experiences.pricing.adjustment_modal.name_tooltip")}
            >
              <Input placeholder={t("experiences.pricing.day_adjustment_modal.name_placeholder")} size="large" />
            </Form.Item>

            <Flex vertical gap={spacings.S}>
              <Text fw={500} size="l">{t("experiences.pricing.adjustment_modal.applies_on")}</Text>
              <Flex vertical gap={spacings.XS}>
                <Radio.Group value={appliesOn} onChange={(e) => setAppliesOn(e.target.value)}>
                  <Flex gap={spacings.XL}>
                    <Radio value={APPLIES_ON.WEEKDAYS}>{t("experiences.pricing.day_adjustment_modal.applies_on_weekdays")}</Radio>
                    <Radio value={APPLIES_ON.WEEKEND}>{t("experiences.pricing.day_adjustment_modal.applies_on_weekend")}</Radio>
                    <Radio value={APPLIES_ON.CUSTOM}>{t("experiences.pricing.day_adjustment_modal.applies_on_custom")}</Radio>
                  </Flex>
                </Radio.Group>

                {appliesOn === APPLIES_ON.CUSTOM && (
                  <Flex gap={spacings.XS} wrap>
                    {WEEKDAYS.map((day) => (
                      <button
                        key={day}
                        type="button"
                        onClick={() => toggleDay(day)}
                        className={`${styles.chip} ${customDays.includes(day) ? styles.chipActive : ""}`}
                      >
                        {day}
                      </button>
                    ))}
                  </Flex>
                )}

                <Flex align="center" gap={spacings.XS} className={styles.infoBox}>
                  <InfoCircleOutlined className={styles.infoBoxIcon} />
                  <Text className={styles.infoBoxText}>
                    {t("experiences.pricing.day_adjustment_modal.applies_on_info")}
                  </Text>
                </Flex>
              </Flex>
            </Flex>

            {/* Affects (ticket-type targeting) is only meaningful for ticketed
                experiences; private trips have no ticket types. */}
            {ticketTypes.length > 0 && (
              <>
                <Divider className={styles.divider} />

                <Flex vertical gap={spacings.M}>
                  <Text fw={500} size="l">{t("experiences.pricing.adjustment_modal.affects")}</Text>
                  <Radio.Group value={affects} onChange={(e) => setAffects(e.target.value)}>
                    <Flex gap={spacings.XL}>
                      <Radio value={AFFECTS.ALL}>{t("experiences.pricing.adjustment_modal.affects_all")}</Radio>
                      <Radio value={AFFECTS.SELECTED}>{t("experiences.pricing.adjustment_modal.affects_selected")}</Radio>
                    </Flex>
                  </Radio.Group>
                  {affects === AFFECTS.SELECTED && (
                    <Flex gap={spacings.XS} wrap>
                      {ticketTypes.map((ticket) => (
                        <button
                          key={ticket.id}
                          type="button"
                          onClick={() => toggleTicket(ticket.id)}
                          className={`${styles.chip} ${selectedTickets.includes(ticket.id) ? styles.chipActive : ""}`}
                        >
                          {ticket.label}
                        </button>
                      ))}
                    </Flex>
                  )}
                </Flex>
              </>
            )}

            <Divider className={styles.divider} />

            <Flex vertical gap={spacings.M}>
              <Text fw={500} size="l">{t("experiences.pricing.adjustment_modal.price_adjustment")}</Text>
              <Radio.Group value={direction} onChange={(e) => setDirection(e.target.value)}>
                <Flex gap={spacings.XL}>
                  <Radio value={PRICE_DIRECTION.INCREASE}>{t("experiences.pricing.adjustment_modal.increase")}</Radio>
                  <Radio value={PRICE_DIRECTION.DECREASE}>{t("experiences.pricing.adjustment_modal.decrease")}</Radio>
                </Flex>
              </Radio.Group>
              <Flex vertical gap={spacings.XS}>
                <Flex align="center" gap={spacings.XS}>
                  <Form.Item
                    name="priceValue"
                    noStyle
                    rules={[{ required: true, message: t("experiences.pricing.adjustment_modal.value_required") }]}
                  >
                    <InputNumber className={styles.priceInput} size="large" min={0} placeholder={"0"} />
                  </Form.Item>
                  <Segmented
                    value={priceType}
                    onChange={setPriceType}
                    options={PRICE_TYPE_OPTIONS}
                    size="large"
                    className={styles.segmented}
                  />
                </Flex>
                <Flex align="center" gap={spacings.XS} className={styles.infoBox}>
                  <InfoCircleOutlined className={styles.infoBoxIcon} />
                  <Text className={styles.infoBoxText}>
                    {t("experiences.pricing.adjustment_modal.base_rate_info")}
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </Card>
      </Form>
    </Modal>
  )
}

AddDayAdjustmentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  editingItem: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    priceValue: PropTypes.number,
    appliesOn: PropTypes.string,
    customDays: PropTypes.arrayOf(PropTypes.string),
    affects: PropTypes.string,
    selectedTickets: PropTypes.arrayOf(PropTypes.string),
    direction: PropTypes.string,
    priceType: PropTypes.string,
  }),
  ticketTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
}

AddDayAdjustmentModal.defaultProps = {
  editingItem: null,
  ticketTypes: PLACEHOLDER_TICKET_TYPES,
}

export default AddDayAdjustmentModal
